// Estado de las pestañas de Obras que sobrevive a cambiar de pestaña y a recargar.
//
// - usePersistentState: filtros y controles de cada vista (localStorage, por navegador
//   y perfil, igual que columnas y filtros de la hoja). El valor guardado se valida al
//   leer: si ya no es válido (opción retirada, JSON roto) se usa el valor por defecto.
// - useTabMemoryState: posición de navegación (ventana del cronograma). Se conserva al
//   cambiar de pestaña, pero al recargar vuelve a "hoy".
import { useEffect, useState } from "react";

const defaultStorage = () => {
    try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
};

export function readStoredValue(storage, key, fallback, validate) {
    try {
        const raw = storage?.getItem(key);
        if (raw == null) return fallback;
        const value = JSON.parse(raw);
        return !validate || validate(value) ? value : fallback;
    } catch {
        return fallback;
    }
}

export function writeStoredValue(storage, key, value) {
    try {
        storage?.setItem(key, JSON.stringify(value));
        return true;
    } catch {
        return false; // cuota llena o almacenamiento bloqueado: el valor vive solo en memoria
    }
}

// options.validate(storedJson) → boolean; options.toJson / fromJson para tipos no JSON (Set).
export function usePersistentState(key, fallback, { validate, toJson, fromJson, storage } = {}) {
    const [value, setValue] = useState(() => {
        const initial = typeof fallback === "function" ? fallback() : fallback;
        const store = storage === undefined ? defaultStorage() : storage;
        const raw = readStoredValue(store, key, undefined, validate);
        if (raw === undefined) return initial;
        return fromJson ? fromJson(raw) : raw;
    });
    useEffect(() => {
        const store = storage === undefined ? defaultStorage() : storage;
        writeStoredValue(store, key, toJson ? toJson(value) : value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, value]);
    return [value, setValue];
}

const tabMemory = new Map();
export function readTabMemory(key, fallback) {
    return tabMemory.has(key) ? tabMemory.get(key) : fallback;
}
export function clearTabMemory() { tabMemory.clear(); }

export function useTabMemoryState(key, fallback) {
    const [value, setValue] = useState(() => readTabMemory(key, typeof fallback === "function" ? fallback() : fallback));
    useEffect(() => { tabMemory.set(key, value); }, [key, value]);
    return [value, setValue];
}

// Validadores habituales.
export const isOneOf = (options) => (value) => options.includes(value);
export const isStringArrayOf = (options) => (value) => Array.isArray(value) && value.every((item) => typeof item === "string" && (!options || options.includes(item)));
export const isMonthOrEmpty = (value) => value === "" || (typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value));
export const isSortState = (fields) => (value) => Boolean(value)
    && typeof value === "object"
    && fields.includes(value.field)
    && (value.direction === "asc" || value.direction === "desc");
